import { useState } from "react";
import { ShieldCheck, CircleAlert } from "lucide-react";

/**
 * Confirmação de identidade.
 *
 * Seis permissões exigem isso (fechar e reabrir folha, exportar base, escrever
 * remuneração, mexer em certificado, remover usuário). Antes, o sistema
 * recusava com a mensagem certa e não oferecia saída: a ação era
 * permanentemente inalcançável.
 *
 * Recusar sem oferecer o caminho é pior que não ter a trava, porque parece um
 * defeito e não uma proteção.
 */
export function StepUp(
  { acao, aoConfirmar, aoFechar }:
  { acao: string; aoConfirmar: () => void; aoFechar: () => void },
) {
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const confirmar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null); setEnviando(true);
    try {
      const r = await fetch("/auth/step-up", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ senha }),
      });
      const corpo = await r.json() as { erro?: string };
      if (!r.ok) { setErro(corpo.erro ?? "Não consegui confirmar."); return; }
      aoConfirmar();
    } catch {
      setErro("Não consegui falar com o servidor.");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true"
         aria-label="Confirmar identidade"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal" style={{ maxWidth: "24rem" }}>
        <header className="painel-topo">
          <h2 className="text-[0.9375rem] font-semibold">Confirme que é você</h2>
          <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
            {acao} exige confirmar a identidade. A confirmação vale 15 minutos e
            só para este aparelho.
          </p>
        </header>
        <form className="painel-corpo space-y-3.5" onSubmit={(e) => void confirmar(e)}>
          <label className="block">
            <span className="eop-rotulo">Sua senha</span>
            <input className="campo mt-1.5" type="password" required autoFocus
                   autoComplete="current-password" value={senha}
                   onChange={(e) => setSenha(e.target.value)} />
          </label>
          {erro && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} /><span>{erro}</span>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="botao" data-variante="silencioso" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="submit" className="botao" data-variante="primario"
                    disabled={enviando || !senha}>
              <ShieldCheck aria-hidden size={15} />
              {enviando ? "Confirmando" : "Confirmar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Reconhece a recusa por step-up sem depender do texto exato da mensagem. */
export const pedeStepUp = (e: unknown): boolean =>
  e instanceof Error && /confirmação da sua identidade/i.test(e.message);
